import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import type { Udalost } from '../api/JsdhNotifikaceService/generated/models/Udalost';
import type { PosilovaNotifikaceLogResponse } from '../api/JsdhNotifikaceService/generated/models/PosilovaNotifikaceLog';
import { ZpravyOdAdmina, maZpravy } from './ZpravyOdAdmina';
import { parsePosila } from '../utils/parsePosila';
import { formatAdresa } from '../utils/adresaUdalosti';
import { FitHeightBox } from './FitHeightBox';
import { Vyjezd2026CasovkaOverlay } from './Vyjezd2026CasovkaOverlay';
import type { Orientace } from '../hooks/useOrientaceKontejneru';

interface Vyjezd2026InfoPanelProps {
    aktivniPoplach: Udalost;
    notifikace: PosilovaNotifikaceLogResponse[];
}

const MAX_POSILY_VISIBLE = 6;

// (parsePosila extrahováno do src/utils/parsePosila.ts pro reuse a unit testy.)

// Auto-fit text: zmenšuje fontSize dokud se text nevejde na jeden řádek (bez wrap).
interface AutoFitTextProps {
    text: string;
    maxFontPx: number;
    minFontPx: number;
    fontWeight: number;
    color?: string;
    textTransform?: 'uppercase' | 'none';
    letterSpacing?: number;
    lineHeight?: number;
}

const AutoFitText: React.FC<AutoFitTextProps> = ({
    text, maxFontPx, minFontPx, fontWeight, color, textTransform = 'none', letterSpacing = 0, lineHeight = 1,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const [fontPx, setFontPx] = useState<number>(maxFontPx);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const fit = () => {
            const containerWidth = container.clientWidth;
            if (containerWidth <= 0) return;
            // Binary search v rozsahu [minFontPx, maxFontPx] na největší velikost která se vejde.
            let lo = minFontPx;
            let hi = maxFontPx;
            let best = minFontPx;
            while (lo <= hi) {
                const mid = Math.floor((lo + hi) / 2);
                measure.style.fontSize = `${mid}px`;
                if (measure.scrollWidth <= containerWidth) {
                    best = mid;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }
            setFontPx(best);
        };

        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(container);
        return () => observer.disconnect();
    }, [text, maxFontPx, minFontPx, fontWeight, textTransform, letterSpacing]);

    return (
        <Box ref={containerRef} sx={{ width: '100%', minWidth: 0 }}>
            {/* Skrytý měřící element — má stejné vlastnosti jako finální */}
            <span
                ref={measureRef}
                style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    whiteSpace: 'nowrap',
                    fontWeight,
                    textTransform,
                    letterSpacing: `${letterSpacing}px`,
                    fontFamily: 'inherit',
                }}
            >
                {text}
            </span>
            <Box
                component="span"
                sx={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    fontSize: `${fontPx}px`,
                    fontWeight,
                    lineHeight,
                    color: color ?? 'inherit',
                    textTransform,
                    letterSpacing: `${letterSpacing}px`,
                    paddingTop: '0.04em',
                }}
            >
                {text}
            </Box>
        </Box>
    );
};

// Multi-line auto-fit: zmenšuje fontSize dokud text zaplní max N řádků bez ořezu.
// Použité pro popis a upresneni — když text je dlouhý, fontSize se zmenší
// (binární vyhledávání mezi minFontPx a maxFontPx). Pokud i při minFontPx text přesahuje
// N řádků, zůstane WebkitLineClamp s ellipsis.
interface AutoFitMultilineProps {
    text: string;            // plain text pro měření (musí odpovídat zobrazovanému obsahu)
    maxFontPx: number;
    minFontPx: number;
    maxLines: number;
    fontWeight: number;
    color?: string;
    lineHeight?: number;
    children?: React.ReactNode;  // volitelně bohatší rendering (např. bold prefix)
}

const AutoFitMultiline: React.FC<AutoFitMultilineProps> = ({
    text, maxFontPx, minFontPx, maxLines, fontWeight, color, lineHeight = 1.25, children,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [fontPx, setFontPx] = useState<number>(maxFontPx);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const fit = () => {
            const w = container.clientWidth;
            if (w <= 0) return;
            measure.style.width = `${w}px`;

            let lo = minFontPx;
            let hi = maxFontPx;
            let best = minFontPx;
            while (lo <= hi) {
                const mid = Math.floor((lo + hi) / 2);
                measure.style.fontSize = `${mid}px`;
                const maxHeight = mid * lineHeight * maxLines;
                if (measure.scrollHeight <= maxHeight + 1) {
                    best = mid;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }
            setFontPx(best);
        };
        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(container);
        return () => observer.disconnect();
    }, [text, maxFontPx, minFontPx, maxLines, fontWeight, lineHeight]);

    return (
        <Box ref={containerRef} sx={{ width: '100%', minWidth: 0, position: 'relative' }}>
            {/* Skrytý měřící prvek */}
            <Box
                ref={measureRef}
                sx={{
                    position: 'absolute', visibility: 'hidden', top: 0, left: 0,
                    fontWeight, lineHeight, wordBreak: 'break-word', whiteSpace: 'normal',
                    fontFamily: 'inherit',
                }}
            >
                {text}
            </Box>
            <Box
                sx={{
                    fontSize: `${fontPx}px`, fontWeight, lineHeight, color: color ?? 'inherit',
                    display: '-webkit-box', WebkitLineClamp: maxLines, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word',
                }}
            >
                {children ?? text}
            </Box>
        </Box>
    );
};

// (InfoRow odstraněn — detaily renderujeme inline níže s tučnou adresou, "Objekt: ..." prefixem atd.)

interface CastProps {
    aktivniPoplach: Udalost;
}

interface HlavickaProps extends CastProps {
    rozlozeni?: Orientace;
    /** Jen na výšku: minuty do výjezdu (barvy a blikání časovky). */
    casVyjezdMinuty?: number;
    /** Jen na výšku: písmo časovky v px (layout ho počítá velikostCasovkyPx; časovka ho omezí na 40–88). */
    casovkaPx?: number;
    /** Jen na výšku: horní odsazení ve výšce banneru vzorového poplachu. */
    odsazeniShoraPx?: number;
}

// Hlavička — typ + podtyp s minimální vertikální mezerou
export const Vyjezd2026Hlavicka: React.FC<HlavickaProps> = ({
    aktivniPoplach, rozlozeni = 'sirka', casVyjezdMinuty = 10, casovkaPx = 88, odsazeniShoraPx = 0,
}) => {
    if (rozlozeni === 'vyska') {
        // Na výšku: písmo v em uvnitř FitHeightBoxu, který hlídá výšku hlavičky (i když část
        // zabírá banner) a jednořádkovou šířku. AutoFitText s px by FitHeightBox zmenšit neuměl.
        return (
            <Box data-testid="vyska-hlavicka" sx={{
                width: '100%', height: '100%', boxSizing: 'border-box',
                display: 'flex', alignItems: 'center', gap: 3,
                pl: '32px', pr: '32px', pb: '12px', pt: `${odsazeniShoraPx + 12}px`,
                bgcolor: 'background.paper', color: 'text.primary',
            }}>
                <FitHeightBox data-testid="vyska-typ-udalosti" maxFontPx={104} minFontPx={40}
                    fitKey={`${aktivniPoplach.pred}|${aktivniPoplach.udalost}`}
                    sx={{ flex: '1 1 0', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'safe center' }}>
                    {aktivniPoplach.pred && (
                        <Box sx={{ fontSize: '1em', fontWeight: 900, lineHeight: 1, letterSpacing: '1px',
                            textTransform: 'uppercase', color: 'error.main', whiteSpace: 'nowrap' }}>
                            {aktivniPoplach.pred}
                        </Box>
                    )}
                    {aktivniPoplach.udalost && (
                        <Box sx={{ fontSize: 'max(20px, 0.423em)', fontWeight: 700, lineHeight: 1.1, mt: '0.1em', whiteSpace: 'nowrap' }}>
                            {aktivniPoplach.udalost}
                        </Box>
                    )}
                </FitHeightBox>
                {aktivniPoplach.casVyhlaseni && (
                    <Vyjezd2026CasovkaOverlay varianta="inline" velikostPx={casovkaPx}
                        casVyhlaseni={aktivniPoplach.casVyhlaseni} casVyjezdMinuty={casVyjezdMinuty} />
                )}
            </Box>
        );
    }
    return (
        <Box data-testid="info-hlavicka">
            {aktivniPoplach.pred && (
                <AutoFitText
                    text={aktivniPoplach.pred}
                    maxFontPx={72}
                    minFontPx={24}
                    fontWeight={900}
                    color="error.main"
                    textTransform="uppercase"
                    letterSpacing={1}
                    lineHeight={0.95}
                />
            )}
            {aktivniPoplach.udalost && (
                <Box sx={{ mt: '-0.1em' }}>
                    <AutoFitText
                        text={aktivniPoplach.udalost}
                        maxFontPx={42}
                        minFontPx={16}
                        fontWeight={700}
                        lineHeight={1}
                    />
                </Box>
            )}
        </Box>
    );
};

interface AdresaProps extends CastProps {
    rozlozeni?: Orientace;
}

// Detaily události — velký font pro čitelnost ze 3 m, inline labely
export const Vyjezd2026Adresa: React.FC<AdresaProps> = ({ aktivniPoplach, rozlozeni = 'sirka' }) => {
    const adresa = formatAdresa(aktivniPoplach);
    if (rozlozeni === 'vyska') {
        const { objekt, popis, upresneni } = aktivniPoplach;
        // Písma v em uvnitř FitHeightBoxu (zmenšuje až na 16 px). Pořadí zkracování určují
        // vlastní FitHeightBoxy s orezRadky a flex-shrink 100/10/1 jako v ZpravyOdAdmina.
        return (
            <FitHeightBox data-testid="vyska-adresa" maxFontPx={46}
                fitKey={`${adresa}|${objekt}|${popis}|${upresneni}`}
                sx={{
                    width: '100%', height: '100%', boxSizing: 'border-box',
                    px: '32px', py: '12px', display: 'flex', flexDirection: 'column', gap: '0.2em',
                    color: 'text.primary', bgcolor: 'background.default',
                }}>
                {adresa && (
                    <Box data-testid="vyska-adresa-text" sx={{
                        flexGrow: 0, flexShrink: 0, flexBasis: 'auto',
                        fontSize: '1em', fontWeight: 700, lineHeight: 1.15, overflowWrap: 'break-word',
                    }}>
                        {adresa}
                    </Box>
                )}
                {objekt && (
                    <FitHeightBox data-testid="vyska-objekt" maxFont="0.652em" orezRadky fitKey={objekt}
                        sx={{ flexGrow: 0, flexShrink: 1, flexBasis: 'auto', maxHeight: '2.5em', lineHeight: 1.25, overflowWrap: 'break-word' }}>
                        <Box component="span" sx={{ color: 'text.secondary' }}>Objekt: </Box>
                        <Box component="span" sx={{ fontWeight: 700 }}>{objekt}</Box>
                    </FitHeightBox>
                )}
                {popis && (
                    <FitHeightBox data-testid="vyska-popis" maxFont="0.609em" orezRadky fitKey={popis}
                        sx={{ flexGrow: 0, flexShrink: 10, flexBasis: 'auto', maxHeight: '5.2em', fontWeight: 600, lineHeight: 1.3, overflowWrap: 'break-word' }}>
                        {popis}
                    </FitHeightBox>
                )}
                {upresneni && (
                    <FitHeightBox data-testid="vyska-upresneni" maxFont="0.522em" orezRadky fitKey={upresneni}
                        sx={{ flexGrow: 0, flexShrink: 100, flexBasis: 'auto', maxHeight: '3.9em', fontWeight: 500, lineHeight: 1.3,
                            color: 'text.secondary', overflowWrap: 'break-word' }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>Upřesnění: </Box>
                        {upresneni}
                    </FitHeightBox>
                )}
            </FitHeightBox>
        );
    }
    return (
        <Box data-testid="info-adresa" sx={{ flex: '0 0 auto' }}>
            {adresa && (
                <Typography sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    lineHeight: 1.15,
                    mb: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    {adresa}
                </Typography>
            )}
            {aktivniPoplach.objekt && (
                <Typography sx={{
                    fontSize: '1.5rem',
                    fontWeight: 500,
                    lineHeight: 1.25,
                    mb: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>Objekt: </Box>
                    <Box component="span" sx={{ fontWeight: 700 }}>{aktivniPoplach.objekt}</Box>
                </Typography>
            )}
            {aktivniPoplach.popis && (
                <Box sx={{ mt: 2 }}>
                    <AutoFitMultiline
                        text={aktivniPoplach.popis}
                        maxFontPx={27}    // 1.7rem
                        minFontPx={16}    // 1rem (zlomová mez čitelnosti)
                        maxLines={4}
                        fontWeight={600}
                        lineHeight={1.25}
                    />
                </Box>
            )}
            {aktivniPoplach.upresneni && (
                <Box sx={{ mt: 0.5 }}>
                    <AutoFitMultiline
                        text={`Upřesnění: ${aktivniPoplach.upresneni}`}
                        maxFontPx={22}    // 1.4rem
                        minFontPx={14}    // 0.875rem
                        maxLines={3}
                        fontWeight={500}
                        lineHeight={1.3}
                        color="text.secondary"
                    >
                        <Box component="span" sx={{ fontWeight: 700 }}>Upřesnění: </Box>
                        {aktivniPoplach.upresneni}
                    </AutoFitMultiline>
                </Box>
            )}
        </Box>
    );
};

interface PosilaZpravyProps extends CastProps {
    notifikace: PosilovaNotifikaceLogResponse[];
    rozlozeni?: Orientace;
}

// Jediné místo rozhodnutí, zda spodní blok (posily + zprávy) na výšku existuje; používá ho
// komponenta níže i rozložení na výšku při přidělování výšky sekcím.
export const maPosilaZpravy = (aktivniPoplach: Udalost, notifikace: PosilovaNotifikaceLogResponse[]): boolean =>
    (aktivniPoplach.dalsiTechnikaJednotek?.length ?? 0) > 0
    || maZpravy(notifikace, aktivniPoplach.posilovaTechnikaTexty);

// Spodní blok (posily + zprávy) — mt:auto kotví ke spodnímu okraji panelu;
// mezera vznikne za "Upřesnění" (mezi detaily a tímto blokem), zprávy a posily těsně nad odpověďmi.
export const Vyjezd2026PosilaZpravy: React.FC<PosilaZpravyProps> = ({ aktivniPoplach, notifikace, rozlozeni = 'sirka' }) => {
    const posily = aktivniPoplach.dalsiTechnikaJednotek || [];
    const posilyVisible = posily.slice(0, MAX_POSILY_VISIBLE);
    const posilyOverflow = posily.length - posilyVisible.length;

    if (rozlozeni === 'vyska') {
        const zpravy = maZpravy(notifikace, aktivniPoplach.posilovaTechnikaTexty);
        if (posily.length === 0 && !zpravy) return null;
        const obeStrany = posily.length > 0 && zpravy;
        return (
            <Box data-testid="vyska-posila-zpravy" sx={{
                width: '100%', height: '100%', boxSizing: 'border-box', minHeight: 0,
                display: 'grid', gridTemplateColumns: obeStrany ? '1fr 1fr' : '1fr', columnGap: 3,
                px: '32px', py: '12px', bgcolor: 'background.paper', color: 'text.primary',
            }}>
                {posily.length > 0 && (
                    <FitHeightBox data-testid="vyska-posily" maxFontPx={22} fitKey={posily.join('|')}
                        sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ fontSize: 'max(16px, 0.82em)', fontWeight: 700, color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: '1px', mb: '0.2em' }}>
                            Technika dalších jednotek
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1em', rowGap: '0.1em' }}>
                            {posilyVisible.map((t, i) => {
                                const { jednotka, typ, volaci } = parsePosila(t);
                                return (
                                    <Box key={i} sx={{ fontSize: '1em', lineHeight: 1.3, overflowWrap: 'break-word' }}>
                                        <Box component="span" sx={{ color: 'text.secondary' }}>{jednotka}: </Box>
                                        <Box component="span" sx={{ fontWeight: 700 }}>{typ}</Box>
                                        {volaci && (
                                            <>
                                                {' '}
                                                <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>{volaci}</Box>
                                            </>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                        {posilyOverflow > 0 && (
                            <Box sx={{ fontSize: '1em', color: 'text.secondary', fontStyle: 'italic', mt: '0.2em' }}>
                                + {posilyOverflow} další techniky
                            </Box>
                        )}
                    </FitHeightBox>
                )}
                {zpravy && (
                    <ZpravyOdAdmina notifikace={notifikace} posilovaTechnikaTexty={aktivniPoplach.posilovaTechnikaTexty}
                        sx={{ height: '100%' }} />
                )}
            </Box>
        );
    }
    return (
        <Box data-testid="info-spodni-blok" sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0, flex: '0 1 auto', minHeight: 0 }}>
            {/* Technika dalších jednotek — 2-sloupcový grid, max 5 viditelných */}
            {posily.length > 0 && (
                <Box sx={{ flex: '0 0 auto' }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography sx={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                    }}>
                        Technika dalších jednotek
                    </Typography>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        columnGap: 2,
                        rowGap: 0.25,
                    }}>
                        {posilyVisible.map((t, i) => {
                            const { jednotka, typ, volaci } = parsePosila(t);
                            return (
                                <Typography
                                    key={i}
                                    component="div"
                                    sx={{
                                        fontSize: '1rem',
                                        lineHeight: 1.35,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                    title={t}
                                >
                                    <Box component="span" sx={{ color: 'text.secondary' }}>{jednotka}: </Box>
                                    <Box component="span" sx={{ fontWeight: 700 }}>{typ}</Box>
                                    {volaci && (
                                        <>
                                            {' '}
                                            <Box component="span" sx={{ color: 'success.main', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                {volaci}
                                            </Box>
                                        </>
                                    )}
                                </Typography>
                            );
                        })}
                    </Box>
                    {posilyOverflow > 0 && (
                        <Typography sx={{ fontSize: '1rem', color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
                            + {posilyOverflow} další techniky
                        </Typography>
                    )}
                </Box>
            )}

            {/* Zprávy od admina + slot POSILA (z logu, případně z povolené posilové techniky).
                Dostanou zbylé místo panelu a zmenší písmo, místo aby se ořízly na 3 řádky. */}
            <ZpravyOdAdmina notifikace={notifikace} posilovaTechnikaTexty={aktivniPoplach.posilovaTechnikaTexty}
                sx={{ flex: '0 1 auto', mt: posily.length > 0 ? 1 : 0 }} />
        </Box>
    );
};

export const Vyjezd2026InfoPanel: React.FC<Vyjezd2026InfoPanelProps> = ({ aktivniPoplach, notifikace }) => (
    <Box sx={{
        width: '100%',
        height: '100%',
        p: 2,
        color: 'text.primary',  // explicit kvůli AutoFitMultiline (Box defaultně nemá MUI color cascade)
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
    }}>
        <Vyjezd2026Hlavicka aktivniPoplach={aktivniPoplach} />
        <Divider />
        <Vyjezd2026Adresa aktivniPoplach={aktivniPoplach} />
        <Vyjezd2026PosilaZpravy aktivniPoplach={aktivniPoplach} notifikace={notifikace} />
    </Box>
);
